import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Divider, Stack, Typography } from "@mui/material";
import DataCell from "@/components/DataCell";
import { format } from "date-fns";
import Price from "@/components/Price";
import { useAppSelector } from "@/hooks";
import { getOrderDetails, getOrderItemPrice, getOrderSubtotal, getOrderTaxes, getOrderTotal } from "@/utils/order";
import { addCurrency } from "@/utils/numbers";
import routes from "@/constants/routes";
import { Order, OrderItem } from "@/types";

interface Props {
  order: Order;
  title: string;
}

const OrderTicket = ({ order }: Props) => {
  const { organizationId } = useParams();
  const currentOrganization = useAppSelector((store) => store.organizations[organizationId] ?? null);

  const showTaxes = useMemo(() => {
    return Boolean(currentOrganization?.settings?.includeTax);
  }, [currentOrganization?.settings?.includeTax]);

  const tips = useMemo(() => {
    if (order?.areTipsIncluded && order?.tipPercentage) {
      return order.areTipsIncluded ? getOrderSubtotal(order.items as OrderItem[]) * (order.tipPercentage / 100) : 0;
    }
    return order?.areTipsIncluded ? 0 : 0;
  }, [order?.areTipsIncluded, order?.items, order?.tipPercentage]);

  return (
    <Stack spacing={2} padding={3}>
      <Stack alignItems={"center"}>
        <Typography>Restaurante</Typography>
        <Typography variant="h2">{currentOrganization?.title}</Typography>
        <Typography>Sucursal</Typography>
      </Stack>
      <Stack border={1} paddingX={2} paddingY={1}>
        <DataCell label="ORDEN" value={order?.number as unknown as string} />
      </Stack>
      <Stack>
        <DataCell label="Fecha" value={order?.createdAt ? format(order.createdAt, "p MM/dd/yyyy") : "N/A"} />
        {/* <DataCell label="Mesa" value="12" /> */}
        {/* <DataCell label="Mesero" value={addCurrency(tips, "NIO")} /> */}
      </Stack>
      <Stack direction={"row"} justifyContent={"space-between"} borderTop={1} borderBottom={1}>
        <Typography> C. Items</Typography>
        <Typography>Montos</Typography>
      </Stack>
      {order?.items?.map((item: OrderItem) => (
        <Stack key={item.id} direction={"row"} spacing={2} justifyContent={"space-between"}>
          <Stack spacing={2} direction={"row"}>
            <Typography flex={1}>{item.quantity}</Typography>
            <div>
              <Typography flex={1}>{item.title}</Typography>
              <Typography>{getOrderDetails(item)}</Typography>
            </div>
          </Stack>
          <Typography variant="body2">
            <Price currency="NIO">{getOrderItemPrice(item)}</Price>
          </Typography>
        </Stack>
        // </Stack>
      ))}
      <Divider sx={{ borderColor: "#000000" }} />
      <Stack>
        <DataCell label="Subtotal" value={addCurrency(getOrderSubtotal(order?.items), "NIO")} />
        <DataCell label="Impuestos (IVA)" value={showTaxes ? addCurrency(getOrderTaxes(order?.items), "NIO") : " c$0"} />
        {/* <DataCell label="Propina (Voluntaria)" value="" /> */}
      </Stack>
      <Stack border={1} paddingX={2} paddingY={1}>
        <DataCell label="TOTAL" value={addCurrency(getOrderTotal(order?.items), "NIO")} />
      </Stack>
      <Stack alignItems={"center"} spacing={2}>
        <Typography>¡Gracias por preferirnos!</Typography>
        <Typography textAlign={"center"}>{`Para delivery o reservas, visita: bitelink.app/${currentOrganization?.alias} ó escanea el código qr:`}</Typography>
        <QRCodeCanvas
          value={`${document.location.origin}${routes.organizations.root.replace(":organizationAlias", currentOrganization?.alias)}`}
          size={100}
          bgColor="#ffffff"
          fgColor="#000000"
        />
      </Stack>
    </Stack>
  );
};

export default OrderTicket;
